import { Report } from "./Report";
import { Asset } from "./Asset";
import { TaskPhase } from "./TaskPhase";
import { estateApi } from "../apis";
import { ProbateType } from "./ProbateType";
import { EstateSummary } from "./EstateSummary";
import { AdvisorGroup, AdvisorGroupJSON } from "./AdvisorGroup";
import { EstateUserInfo, EstateUserInfoJson } from "./EstateUserInfo";
import { objectToSnake } from "ts-case-convert";

// database field names
export type EstateJSON = {
  id?: null | number;
  // relationship?: null | string;
  // relationship_more?: null | string;
  // advisory_position?: null | string;
  // advisory_position_more?: null | string;
  user_id?: null | number;
  advisor_group_id?: null | number;
  // charge_id: "fake_charge_id"
  deceased_first_name?: null | string;
  deceased_middle_name?: null | string;
  deceased_last_name?: null | string;
  deceased_suffix?: null | string;
  estate_id?: null | number; // de-conflict id with estate_id
  executor_relationship_status?: null | "Great";
  has_business_ownerships?: null | boolean;
  has_investment_accounts?: null | boolean;
  has_life_insurance?: null | boolean;
  has_out_of_state_assets?: null | boolean; // convert to boolean
  has_passed?: null | string; // convert to boolean
  has_real_estate?: null | boolean;
  has_trust?: null | boolean;
  has_will?: null | string;
  leave_minors?: null | "YES"; // convert to boolean
  leave_pets?: null | "YES"; // convert to boolean
  passed_on?: null | string; // convert to date
  was_veteran?: null | string; // convert to boolean
  avatar_key?: null | string;
  executor_first?: null | string;
  executor_middle?: null | string;
  executor_last?: null | string;
  executor_suffix?: null | string;
  executor_email?: null | string;
  executor_phone?: null | string;
  address?: null | string;
  country_id?: null | number;
  state_id?: null | number;
  property_ownership?: null | string;
  property_value?: null | number;
  letters?: null | string;
  tasks_completed?: null | string; // json string to be converted to string[]
  type_of_assets?: null | string; // json string to be converted to string[]
  estate_team?: null | string; // json string to be converted to string[]
  need_help_with?: null | string; // json string to be converted to string[]
  need_help_with_something_else?: null | string;
  county?: null | string;
  value_below_threshold?: null | string;
  // declared_role?: null | string;
  already_started?: null | boolean;
  probate_type_id?: null | number | null;
  probate_type?: null | ProbateType;
  latest_activity?: null | string;
  users_profile_id?: null | number;
  deleted_at?: null | string;
  is_demo?: null | boolean;
  primary_home_complete?: null | boolean;
  advisor_group?: null | AdvisorGroupJSON;
  estate_user_info?: null | EstateUserInfoJson;
};

export interface NameValues {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
}

export class Estate {
  constructor(
    public id?: null | number,
    public userId?: null | number,
    public userProfileId?: null | number,
    public deceasedFirstName?: null | string,
    public deceasedMiddleName?: null | string,
    public deceasedLastName?: null | string,
    public deceasedSuffix?: null | string,
    // public relationship?: null | string, // = "child",
    // public relationshipMore?: null | string,
    public hasWill?: null | string,
    public propertyOwnership?: null | string,
    public propertyValue?: null | number,
    public letters?: null | string,
    public passedOn?: null | string,
    public avatarKey?: null | string,
    public executorFirst?: null | string,
    public executorMiddle?: null | string,
    public executorLast?: null | string,
    public executorSuffix?: null | string,
    public executorEmail?: null | string,
    public executorPhone?: null | string,
    public address?: null | string, // full address string: "1423 Elrondo Village Drive, San Diego, CA 92109"
    public countryId?: null | number,
    public stateId?: null | number,
    public county?: null | string,
    public valueBelowThreshold?: null | string,
    // public declaredRole?: null | string,
    public hasPassed?: null | boolean,
    public alreadyStarted?: null | boolean,
    public advisorGroupId?: null | number,
    public advisorGroup?: null | AdvisorGroup,
    public estateUserInfo?: null | EstateUserInfo,
    // public advisorGroupName?: null | string,
    // public numRelationships?: null | number,
    // public fairMarketValue?: null | number,
    // NOTE: opportunities are assets of a certain type?
    public assets?: null | Asset[] | null,
    // public opportunities?: null | Opportunity[],
    // public notes?: null | EstateNote[] | null,
    public reports?: null | Report[] | null,
    public taskPhases?: null | TaskPhase[] | null,
    public estateSummary?: null | EstateSummary | null,
    public tasksCompleted?: null | string[],
    public typeOfAssets?: null | string[],
    public estateTeam?: null | string[],
    public needHelpWith?: null | string[],
    public needHelpWithSomeThingElse?: null | string,
    public probateType?: null | ProbateType,
    public updatedAt?: null | string,
    public deletedAt?: null | string,
    public isDemo?: null | boolean,
    public wasVeteran?: null | string,
    public primaryHomeComplete?: null | boolean,
    public hasRealEstate?: null | boolean, // support legacy
    public hasInvestmentAccounts?: null | boolean, // support legacy
    public hasLifeInsurance?: null | boolean, // support legacy
    public hasBusinesses?: null | boolean, // support legacy
    public hasOutOfStateAssets?: null | boolean, // support legacy
    public hasTrust?: null | boolean // support legacy
  ) {}

  // clone
  static from(estate: Estate): Estate {
    return new Estate(
      estate.id,
      estate.userId,
      estate.userProfileId,
      estate.deceasedFirstName,
      estate.deceasedMiddleName,
      estate.deceasedLastName,
      estate.deceasedSuffix,
      // estate.relationship, // = "child",
      // estate.relationshipMore,
      estate.hasWill,
      estate.propertyOwnership,
      estate.propertyValue,
      estate.letters,
      estate.passedOn,
      estate.avatarKey,
      estate.executorFirst,
      estate.executorMiddle,
      estate.executorLast,
      estate.executorSuffix,
      estate.executorEmail,
      estate.executorPhone,
      estate.address,
      estate.countryId,
      estate.stateId,
      estate.county,
      estate.valueBelowThreshold,
      // estate.declaredRole,
      estate.hasPassed,
      estate.alreadyStarted,
      estate.advisorGroupId,
      estate.advisorGroup,
      estate.estateUserInfo,
      // estate.numRelationships,
      // estate.fairMarketValue,
      // NOTE: opportunities are assets of a certain type?
      estate.assets,
      // estate.opportunities?: null | Opportunity[],
      // estate.notes?: null | EstateNote[] | null,
      estate.reports,
      estate.taskPhases,
      estate.estateSummary,
      estate.tasksCompleted,
      estate.typeOfAssets,
      estate.estateTeam,
      estate.needHelpWith,
      estate.needHelpWithSomeThingElse,
      estate.probateType,
      estate.updatedAt,
      estate.deletedAt,
      estate.isDemo,
      estate.wasVeteran,
      estate.primaryHomeComplete,
      estate.hasRealEstate,
      estate.hasInvestmentAccounts,
      estate.hasLifeInsurance,
      estate.hasBusinesses,
      estate.hasOutOfStateAssets,
      estate.hasTrust
    );
  }

  static fromJSON = (json: EstateJSON): Estate => {
    return new Estate(
      json.id,
      json.user_id,
      json.users_profile_id,
      json.deceased_first_name,
      json.deceased_middle_name,
      json.deceased_last_name,
      json.deceased_suffix,
      // json.relationship,
      // json.relationship_more,
      json.has_will,
      json.property_ownership,
      json.property_value,
      json.letters,
      json.passed_on,
      json.avatar_key,
      json.executor_first,
      json.executor_middle,
      json.executor_last,
      json.executor_suffix,
      json.executor_email,
      json.executor_phone,
      json.address,
      json.country_id,
      json.state_id,
      json.county,
      json.value_below_threshold,
      // json.declared_role,
      json.has_passed === "1" || (typeof json.has_passed === "string" && json.has_passed.toLowerCase() === "yes"),
      json.already_started,
      json.advisor_group_id,
      json.advisor_group
        ? AdvisorGroup.fromJSON(json.advisor_group)
        : undefined,
      json.estate_user_info
        ? EstateUserInfo.fromJSON(json.estate_user_info)
        : undefined,
      // undefined,
      undefined,
      undefined,
      // undefined,
      undefined,
      undefined,
      typeof json.tasks_completed === "string" ? JSON.parse(json.tasks_completed) : json.tasks_completed || undefined,
      typeof json.type_of_assets === "string" ? JSON.parse(json.type_of_assets) : json.type_of_assets || undefined,
      typeof json.estate_team === "string" ? JSON.parse(json.estate_team) : json.estate_team || undefined,
      typeof json.need_help_with === "string" ? JSON.parse(json.need_help_with) : json.need_help_with || undefined,
      json.need_help_with_something_else,
      typeof json.probate_type === "string" ? JSON.parse(json.probate_type) : json.probate_type || undefined,
      json.latest_activity,
      json.deleted_at,
      json.is_demo,
      json.was_veteran,
      json.primary_home_complete,
      json.has_real_estate,
      json.has_investment_accounts,
      json.has_investment_accounts,
      json.has_business_ownerships,
      json.has_out_of_state_assets,
      json.has_trust
    );
  };

  toJson(): EstateJSON {
    const skipKeys:{[key:string]:boolean} = {
      "path": true,
      "executor_first": true,
      "executor_middle": true,
      "executor_last": true,
      "executor_suffix": true,
      "executor_email": true,
      "executor_phone": true,
    }
    const json: any = {};
    const thisObject = JSON.parse(JSON.stringify(this))
    const snakeCaseObject = objectToSnake(thisObject) as any
    Object.keys(snakeCaseObject).map((k:string) => {
      if(skipKeys[k]) {
        console.log("skipping estate json key " + k)
        return
      }

      const val = snakeCaseObject[k]
      if(val === undefined) {
        return
      }

      json[k] = val
    })
    if(json.was_veteran === "1" || (typeof json.was_veteran === "string" && json.was_veteran.toLowerCase() === "yes")) {
      json.was_veteran = "Yes"
    } else if(json.was_veteran === "0" || (typeof json.was_veteran === "string" && json.was_veteran.toLowerCase() === "no")) {
      json.was_veteran = "No"
    } 


    if(json.has_will && typeof json.has_will === "string") {

      if(["Yes", "No", "I don't know"].includes(json.has_will)) {
        // keep as is
      } else if(json.has_will.toLowerCase() === "yes") {
        json.has_will = "Yes"
      } else if(json.has_will.toLowerCase() === "no") {
        json.has_will = "No"
      } else if(json.has_will.toLowerCase().includes("don't know") || json.has_will.toLowerCase().includes("dont know")) {
        json.has_will = "I don't know"
      }
    }

    return json;
  }

  get nameValues(): NameValues {
    return {
      firstName: this?.deceasedFirstName || "",
      middleName: this?.deceasedMiddleName || "",
      lastName: this?.deceasedLastName || "",
      suffix: this?.deceasedSuffix || "",
    };
  }

  async saveEstate(userId?:number): Promise<Estate> {
    const fields = this.toJson();
    if(!fields.country_id) {
      fields.country_id = undefined
      fields.state_id = undefined
    }
    if(!fields.state_id) {
      fields.state_id = undefined
    }
    if (this?.id) {
      await estateApi.updateEstate(this.id, fields);
    } else if (userId) {
      this.id = await estateApi.createEstate(userId,fields);
    } else {
      console.error("no estate id and no user id");
    }
    return this;
  }

  get primaryRealEstate(): Asset | undefined {
    let asset = undefined;
    if (this.assets) {
      const reAssets = this.assets?.filter(
        (asset: Asset) => asset.category?.category === "Real Estate"
      );
      const primaryHomeIndex = reAssets?.findIndex(
        (asset: Asset) => asset.subcategory?.subcategory === "Primary Home"
      );
      if (primaryHomeIndex > 0) {
        asset = reAssets[primaryHomeIndex];
      } else {
        // return the first real estate asset
        asset = reAssets[0];
      }
    }
    return asset;
  }

  get name(): null | string | undefined {
    let retName = this.deceasedFirstName;
    if (this.deceasedMiddleName) {
      retName += ` ${this.deceasedMiddleName}`;
    }
    if (this.deceasedLastName) {
      retName += ` ${this.deceasedLastName}`;
    }
    if (this.deceasedSuffix) {
      retName += `, ${this.deceasedSuffix}`;
    }
    return retName;
  }
}
